import {
  Autocomplete,
  Checkbox,
  FormControl,
  Grid,
  IconButton,
  ListItem,
  ListItemText,
  Typography,
} from "@mui/material";
import { isFulfilled, nanoid } from "@reduxjs/toolkit";
import React, { useContext, useEffect, useState } from "react";
import {
  Controller,
  FormProvider,
  useFieldArray,
  useForm,
  useFormContext,
} from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "../../../../../../app/hooks";
import AddButton from "../../../../../../common/components/functional/addButton/AddButton";
import CommonTextField from "../../../../../../common/components/functional/commonTextField/CommonTextField";
import { ConfirmationDialog } from "../../../../../../common/components/functional/confirmationDialog/ConfirmationDialog";
import AutocompleteChip from "../../../../../../common/components/functional/customAutocomplete/AutocompleteChip";
import DialogContentWrapper from "../../../../../../common/components/functional/dialogs/DialogContentWrapper";
import LoadingSpinner from "../../../../../../common/components/functional/loadingSpinner/LoadingSpinner";
import isEmpty from "../../../../../../common/helpers/isEmpty";
import { ReactComponent as DeleteIcon } from "../../../../../../common/icons/deleteDark.svg";
import { VerticalAlignGrid } from "../../../../../invoices/styled";
import { AccessRightContext } from "../../../../../user/AccessRightProvider";
import { fetchTesExpenseTypeCodes } from "../../../../customerContractsSlice";
import {
  EMPTY_PRICING_BY_ITEM,
  HIDE_OVERRIDE_AND_FIXED_PRICE_FOR_SALARY_TYPE,
  PricingCategory,
  PricingCategorySelection,
  SHOW_ONLY_FOR_HOURLY_BILLING_LABEL_FOR_SALARY_TYPE,
} from "../../../constants";
import LabelInputSelectRow from "../../../form/LabelInputSelectRow";
import {
  convertExpensesDataArrayToObjects,
  convertExpensesDataObjectsToArray,
  getAllSelectedIds,
  getExpenseTypeCategoryKey,
} from "../../../helpers/pricingUtils";
import { UnsavedChangesProvider } from "../../../../../../contexts/UnsavedChangesContext";
import { PricingGuideText } from "../../../styles";

interface Props {
  tesId: string;
  mainFormPropertyKey: string;
  expenseTypeCategoryFilter: PricingCategory;
  modalTitle: string;
  rowLabel: string;
  inputLabel: string;
  addButtonLabel: string;
  confirmDialogTitle: string;
  confirmDialogContentText: string;
  disabled?: boolean;
  onClose: () => void;
  canEdit: boolean;
  hideOverrideOption?: boolean;
  hideMultiplierOption?: boolean;
  hideFixedPriceOption?: boolean;
  showOnlyForHourlyBillingLabel?: boolean;
}

const LOCAL_FORM_PROPERTY_KEY = "pricingsByExpenseTypeArray";
const CONFIRMATION_DIALOG_INITIAL_VALUES = {
  pendingAction: () => {},
  open: false,
};

const PricingsByExpenseTypeForm = ({
  tesId,
  mainFormPropertyKey,
  expenseTypeCategoryFilter,
  modalTitle,
  rowLabel,
  inputLabel,
  addButtonLabel,
  confirmDialogTitle,
  confirmDialogContentText,
  disabled,
  onClose,
  canEdit,
  hideOverrideOption = false,
  hideMultiplierOption = false,
  hideFixedPriceOption = false,
  showOnlyForHourlyBillingLabel = false,
}: Props) => {
  const { t } = useTranslation("customers");
  const { canEditContract } = useContext(AccessRightContext);
  const { expenseType: expenseTypeLocalized } = useAppSelector(
    (state) => state.localized,
  );
  const dispatch = useAppDispatch();
  const [isDataLoading, setIsDataLoading] = useState<boolean>(true);
  const [expenseTypeOptions, setExpenseTypeOptions] = useState<string[]>([]);
  const [confirmationDialog, setConfirmationDialog] = useState<{
    pendingAction: null | any;
    open: boolean;
  }>(CONFIRMATION_DIALOG_INITIAL_VALUES);

  const { getValues: getMainFormValues, setValue: setMainFormValue } =
    useFormContext();

  // Get expense type codes for the given TES and expense type category
  useEffect(() => {
    dispatch(
      fetchTesExpenseTypeCodes({
        tesId: tesId,
        pricingCategory: getExpenseTypeCategoryKey(expenseTypeCategoryFilter),
      }),
    )
      .then((action) => {
        if (isFulfilled(action)) {
          const tesExpenseTypeCodes = action.payload.map((item) => item.code);
          setExpenseTypeOptions(tesExpenseTypeCodes);
        }
      })
      .finally(() => {
        setIsDataLoading(false);
      });
  }, [dispatch, tesId, expenseTypeCategoryFilter]);

  const existingData = getMainFormValues(mainFormPropertyKey);
  const defaultValues = {
    [LOCAL_FORM_PROPERTY_KEY]: convertExpensesDataObjectsToArray(
      existingData?.expenses || {},
    ),
  };

  const methods = useForm({
    defaultValues,
    mode: "onChange",
  });

  const {
    control,
    handleSubmit,
    setError,
    clearErrors,
    formState: { isDirty, errors },
  } = methods;

  const {
    fields: pricingsByExpenseType,
    append,
    remove,
    update,
  } = useFieldArray({
    control,
    name: LOCAL_FORM_PROPERTY_KEY,
  });

  const allSelectedExpenseTypeIds = getAllSelectedIds(pricingsByExpenseType);

  const getSelectOptions = (pricingObject: any) => {
    const hideOptionPerRow = pricingObject.ids.some((id: string) =>
      HIDE_OVERRIDE_AND_FIXED_PRICE_FOR_SALARY_TYPE.includes(id),
    );
    return [
      ...(hideOverrideOption || hideOptionPerRow
        ? []
        : [PricingCategorySelection.Override]),
      ...(hideMultiplierOption ? [] : [PricingCategorySelection.Multiplier]),
      ...(hideFixedPriceOption || hideOptionPerRow
        ? []
        : [PricingCategorySelection.FixedPrice]),
      PricingCategorySelection.NotBilled,
    ];
  };

  const showHourlyBillingLabel = (pricingObject: any) => {
    const showHourlyBillingLabelPerRow = pricingObject.ids.some((id: string) =>
      SHOW_ONLY_FOR_HOURLY_BILLING_LABEL_FOR_SALARY_TYPE.includes(id),
    );
    return showOnlyForHourlyBillingLabel || showHourlyBillingLabelPerRow;
  };

  const onSubmit = (formData: any) => {
    const expensesArray = formData[LOCAL_FORM_PROPERTY_KEY];
    const expensesObjects = convertExpensesDataArrayToObjects(expensesArray);

    const updatedData = {
      multiplier: existingData?.multiplier,
      ...(!isEmpty(expensesObjects) && { expenses: expensesObjects }),
    };

    setMainFormValue(mainFormPropertyKey, updatedData, {
      shouldDirty: true,
    });

    onClose();
  };

  const onSave = () => {
    handleSubmit(onSubmit)();
  };

  const onCancel = () => onClose();

  const getErrorId = (pricingObject: any) => pricingObject.itemId as any;

  const handleSelect = (
    pricingObject: any,
    newSelection: string,
    index: number,
  ) => {
    const errorId = getErrorId(pricingObject);
    newSelection === PricingCategorySelection.NotBilled
      ? clearErrors(errorId)
      : setError(errorId, {});
    update(index, {
      ...pricingObject,
      type: newSelection, // PricingCategorySelection enum
      multiplier:
        newSelection === PricingCategorySelection.NotBilled ? "0" : "",
    });
  };

  const handleAddPricing = () => {
    const newId = nanoid();
    const newObject = {
      itemId: newId,
      ...EMPTY_PRICING_BY_ITEM,
    };
    // Add new object to fields array
    append(newObject);
    // Use itemId (newId) for error tracking, set error for new empty item
    setError(newId as any, {});
  };

  const askForConfirmation = (pendingAction: any) => {
    setConfirmationDialog({ pendingAction, open: true });
  };

  const handleRemovePricingSet = (index: number, itemId: string) => {
    askForConfirmation(() => {
      // Remove item and its errors
      remove(index);
      clearErrors(itemId as any);
    });
  };

  const addPricingDisabled =
    allSelectedExpenseTypeIds.length === expenseTypeOptions.length;

  const saveDisabled = !isDirty || !isEmpty(errors);

  return (
    <DialogContentWrapper
      title={modalTitle}
      margin
      onCancel={onCancel}
      onSave={onSave}
      saveDisabled={saveDisabled || !canEditContract}
      childContainerSx={{ overflowY: "auto" }}
    >
      <FormProvider {...methods}>
        <UnsavedChangesProvider isDirty={methods.formState.isDirty}>
          <PricingGuideText padding="0 1rem 0 1rem">
            {!hideOverrideOption
              ? t("contract.pricingBySalaryTypeGuideText")
              : t("contract.pricingBySalaryTypeGuideTextWithoutOverride")}
          </PricingGuideText>
          {isDataLoading ? (
            <LoadingSpinner sx={{ height: "80px" }} />
          ) : (
            <>
              {pricingsByExpenseType.map((pricingObject: any, index) => (
                <React.Fragment key={`${LOCAL_FORM_PROPERTY_KEY}[${index}].id`}>
                  <VerticalAlignGrid
                    container
                    spacing={1}
                    sx={{ marginTop: index === 0 ? "0.5rem" : " " }}
                  >
                    <Grid
                      container
                      item
                      xs={3}
                      justifyContent="flex-end"
                      paddingRight="0.5rem"
                    >
                      <Typography variant="secondary">{rowLabel}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <FormControl fullWidth size="small">
                        <Controller
                          name={`${LOCAL_FORM_PROPERTY_KEY}`}
                          control={control}
                          render={() => {
                            return (
                              <Autocomplete
                                multiple
                                disableCloseOnSelect
                                disabled={disabled}
                                value={pricingObject.ids}
                                getOptionLabel={(expenseTypeId) =>
                                  expenseTypeLocalized[expenseTypeId]
                                }
                                options={expenseTypeOptions}
                                getOptionDisabled={(expenseTypeId) => {
                                  // Let user remove selections for current row
                                  if (
                                    pricingObject.ids.includes(expenseTypeId)
                                  ) {
                                    return false;
                                  }
                                  // Disable selection if it's already selected in another row
                                  return allSelectedExpenseTypeIds.includes(
                                    expenseTypeId,
                                  );
                                }}
                                renderOption={(props, expenseTypeId) => (
                                  <ListItem {...props} key={expenseTypeId}>
                                    <Checkbox
                                      checked={
                                        pricingObject.ids.indexOf(
                                          expenseTypeId,
                                        ) > -1
                                      }
                                    />
                                    <ListItemText>
                                      {expenseTypeLocalized[expenseTypeId]}
                                    </ListItemText>
                                  </ListItem>
                                )}
                                renderInput={(params) => (
                                  <CommonTextField
                                    {...params}
                                    label={inputLabel}
                                    error={pricingObject.ids.length === 0}
                                  />
                                )}
                                renderTags={(value, getTagProps) =>
                                  value.map((expenseTypeId, index) => {
                                    const { key, ...restProps } = getTagProps({
                                      index,
                                    });
                                    return (
                                      <AutocompleteChip
                                        key={key}
                                        label={
                                          expenseTypeLocalized[expenseTypeId]
                                        }
                                        {...restProps}
                                      />
                                    );
                                  })
                                }
                                onChange={(_, newExpenseTypeIds) => {
                                  const errorId = getErrorId(pricingObject);
                                  // Check if removing item
                                  const removingItem =
                                    newExpenseTypeIds.length <
                                    pricingObject.ids.length;
                                  if (removingItem) {
                                    askForConfirmation(() => {
                                      update(index, {
                                        ...pricingObject,
                                        ids: newExpenseTypeIds,
                                      });
                                      // If ids array is empty, set error for the item
                                      if (newExpenseTypeIds.length === 0) {
                                        setError(errorId, {});
                                      }
                                    });
                                    // Adding item
                                  } else {
                                    update(index, {
                                      ...pricingObject,
                                      ids: newExpenseTypeIds,
                                    });
                                    if (!isEmpty(pricingObject.multiplier)) {
                                      clearErrors(errorId);
                                    }
                                  }
                                }}
                              />
                            );
                          }}
                        />
                      </FormControl>
                    </Grid>
                    <Grid item>
                      {!disabled && (
                        <IconButton
                          onClick={() =>
                            handleRemovePricingSet(index, pricingObject.itemId)
                          }
                        >
                          <DeleteIcon />
                        </IconButton>
                      )}
                    </Grid>
                  </VerticalAlignGrid>
                  <LabelInputSelectRow
                    label=""
                    selectOptions={getSelectOptions(pricingObject)}
                    inputValue={pricingObject.multiplier}
                    inputRequired
                    onInputChange={(newInput: string) => {
                      const errorId = getErrorId(pricingObject);
                      if (!newInput || pricingObject.ids.length === 0) {
                        setError(errorId, {});
                      } else {
                        clearErrors(errorId);
                      }
                      update(index, { ...pricingObject, multiplier: newInput });
                    }}
                    defaultSelection={pricingObject.type}
                    onSelectionChange={(newSelection: string) =>
                      handleSelect(pricingObject, newSelection, index)
                    }
                    inputWidth={3}
                    selectWidth={3}
                    selectSx={{
                      ".MuiSelect-select": {
                        whiteSpace: "normal",
                      },
                    }}
                    disabled={disabled}
                    showOnlyForHourlyBillingLabel={showHourlyBillingLabel(
                      pricingObject,
                    )}
                  />
                </React.Fragment>
              ))}
              <Grid container>
                <Grid item xs={3} />
                <Grid sx={{ paddingTop: "0.5rem" }}>
                  <AddButton
                    accessRightChecks={[canEdit]}
                    label={addButtonLabel}
                    onClick={handleAddPricing}
                    disabled={addPricingDisabled || disabled}
                  />
                </Grid>
              </Grid>
            </>
          )}
        </UnsavedChangesProvider>
      </FormProvider>
      <ConfirmationDialog
        title={confirmDialogTitle}
        contentText={confirmDialogContentText}
        cancelText={t("common:cancel")}
        confirmText={t("common:delete")}
        isOpen={confirmationDialog.open}
        onConfirm={() => {
          confirmationDialog.pendingAction();
          setConfirmationDialog(CONFIRMATION_DIALOG_INITIAL_VALUES);
        }}
        onCancel={() =>
          setConfirmationDialog(CONFIRMATION_DIALOG_INITIAL_VALUES)
        }
      />
    </DialogContentWrapper>
  );
};

export default PricingsByExpenseTypeForm;
